import * as Yup from 'yup';

import { optionalStringValidation, requiredStringValidation } from './strings';

import { isLocal, isStaging } from '../../config';

export const ssnRegExpLast4Min =
  isLocal || isStaging
    ? /^[X_0-9]{3}-[X_0-9]{2}-\d{4}$/
    : /^(?!(000|666|9))[X_0-9]{3}-(?!00)[X_0-9]{2}-(?!0000)\d{4}$/;
export const ssnRegExpLast6Min =
  isLocal || isStaging
    ? /^[X_0-9]{3}-[0-9]{2}-\d{4}$/
    : /^(?!(000|666|9))[X_0-9]{3}-(?!00)[0-9]{2}-(?!0000)\d{4}$/;
export const ssnRegExpFull =
  isLocal || isStaging
    ? /^[0-9]{3}-[0-9]{2}-\d{4}$/
    : /^(?!(000|666|9))[0-9]{3}-(?!00)[0-9]{2}-(?!0000)\d{4}$/;
export const optionalSsnMaskRegExp = /^(XXX-XX-XXXX|___-__-____|\d{3}-\d{2}-\d{4}|)$/;

const itinRegExFull =
  isLocal || isStaging
    ? /^9\d{2}-\d{2}-\d{4}$/ // for testing envs, only ensure it starts with 9 and is 9 digits
    : /^9\d{2}-(?:5[0-9]|6[0-5]|7[0-9]|8[0-8]|9[0-2]|9[4-9])-\d{4}$/;

const isValidSsnOrItin = (value: string) => ssnRegExpFull.test(value) || itinRegExFull.test(value);

export const validateSsn = (isCobuyer: boolean, regEx: RegExp, optional?: boolean) => {
  if (optional) {
    return optionalStringValidation.matches(optionalSsnMaskRegExp, `Please input a valid SSN`);
  }

  // Instead of implementing ITINs separately, just allow both SSN and ITINs
  if (regEx === ssnRegExpFull) {
    return requiredStringValidation(`${isCobuyer ? 'Cobuyer' : ''}SSN is required`).test(
      'ssn-or-itin',
      `Please input a valid SSN or ITIN`,
      (value) => (typeof value === 'string' ? isValidSsnOrItin(value) : false),
    );
  }

  return requiredStringValidation(`${isCobuyer ? 'Cobuyer' : ''}SSN is required`).matches(
    regEx,
    `Please input a valid SSN`,
  );
};

export const hasFullSsnValidation = (person?: string) =>
  Yup.boolean()
    .nullable()
    .isTrue(`Please enter a valid SSN ${person ? `for ${person}` : ''}`);

export const ssnValidation = (person?: string) => ({
  ssn_id: Yup.number()
    .nullable()
    .required(`${person || ''}SSN is required`),
  has_full_ssn: Yup.boolean()
    .nullable()
    .when('ssn_id', {
      is: (ssn_id: number) => ssn_id != null,
      then: hasFullSsnValidation(person),
      otherwise: Yup.boolean().nullable(),
    }),
});

export const isSsnAnItin = (ssn: string) => {
  const cleanedSsn = ssn.trim().replace(/\D/g, '');
  return cleanedSsn.length === 9 && cleanedSsn.charAt(0) === '9';
};
